// /api/weekly-summaries — Session 7 PR4. Drives the Home WeeklySummaryTile.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./api";
import { useMutationToast } from "../hooks/useMutationToast";

export interface WeekActivity {
  new_applications: number;
  status_changes: number;
  interviews_added: number;
  notes_added: number;
  messages_sent: number;
}

export interface WeeklySummaryStats {
  generated_at: number;
  week_start: number;
  week_end: number;
  funnel: Record<string, number>;
  overall: { submitted: number; responded: number; rate: number };
  stuck_count: number;
  progressed_count: number;
  this_week: WeekActivity;
  prev_week: WeekActivity;
}

export interface WeeklySummary {
  id: string;
  week_start: number;
  narrative: string;
  model: string;
  created_at: number;
  dismissed_at: number | null;
  // The route encodes stats_json as parsed JSON. A row with corrupt JSON
  // (shouldn't happen in practice) is sent as null so the tile still renders
  // the narrative.
  stats: WeeklySummaryStats | null;
}

export interface WeeklySummariesResponse {
  summaries: WeeklySummary[];
}

export function useWeeklySummaries() {
  return useQuery({
    queryKey: ["weekly-summaries"],
    queryFn: () => api<WeeklySummariesResponse>("/api/weekly-summaries"),
    // The Monday cron writes once per week; nothing changes on a sub-minute
    // cadence. 5min staleTime keeps Home navigations cheap without staling
    // out a fresh regenerate.
    staleTime: 5 * 60 * 1000,
  });
}

export function useDismissWeeklySummary() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<{ ok: boolean }>(`/api/weekly-summaries/${id}/dismiss`, {
        method: "POST",
      }),
    // Optimistic: flip dismissed_at locally so the tile vanishes
    // immediately rather than after the round-trip.
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: ["weekly-summaries"] });
      const prev = qc.getQueryData<WeeklySummariesResponse>(["weekly-summaries"]);
      if (prev) {
        qc.setQueryData<WeeklySummariesResponse>(["weekly-summaries"], {
          summaries: prev.summaries.map((s) =>
            s.id === id ? { ...s, dismissed_at: Date.now() } : s,
          ),
        });
      }
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) {
        qc.setQueryData(["weekly-summaries"], ctx.prev);
      }
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["weekly-summaries"] });
    },
  });
}

export function useRegenerateWeeklySummary() {
  const qc = useQueryClient();
  return useMutationToast<{ summary: WeeklySummary }, Error, void>({
    mutationFn: () =>
      api<{ summary: WeeklySummary }>("/api/weekly-summaries/regenerate", {
        method: "POST",
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["weekly-summaries"] });
    },
    success: "Weekly summary regenerated.",
    errorPrefix: "Regenerate failed",
  });
}
